import { Box, Stack, Typography, useTheme } from "@mui/material";
import { IDarkTheme } from "styles/darkTheme";

export type BillingPeriod = "monthly" | "yearly";

interface IBillingToggleProps {
  value: BillingPeriod;
  onChange: (value: BillingPeriod) => void;
  savingsPercentage?: number;
}

const BillingToggle = ({
  value,
  onChange,
  savingsPercentage
}: IBillingToggleProps) => {
  const theme = useTheme<IDarkTheme>();

  return (
    <Stack
      direction="column"
      alignItems="center"
      gap={2}
      sx={{ marginBottom: "32px" }}
    >
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          padding: "6px",
          backgroundColor: theme.colors.neutrals.neutrals_10,
          borderRadius: "12px",
          border: `1px solid ${theme.colors.neutrals.neutrals_30}`,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)"
        }}
      >
        {/* Sliding background indicator */}
        <Box
          sx={{
            position: "absolute",
            top: "6px",
            left: value === "monthly" ? "6px" : "calc(50%)",
            width: "calc(50% - 6px)",
            height: "calc(100% - 12px)",
            backgroundColor: theme.colors.secondary.secondary_50,
            borderRadius: "8px",
            boxShadow: "0px 2px 6px rgba(102, 126, 234, 0.3)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            zIndex: 0
          }}
        />

        {/* Monthly Button */}
        <Box
          component="button"
          onClick={() => onChange("monthly")}
          sx={{
            position: "relative",
            zIndex: 1,
            padding: "12px 32px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            transition: "all 0.3s ease",
            borderRadius: "8px",
            minWidth: "140px",
            "&:hover": {
              transform: value === "monthly" ? "none" : "scale(1.02)"
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.colors.secondary.secondary_50}`,
              outlineOffset: "2px"
            }
          }}
        >
          <Typography
            fontFamily={theme.fontFamily.montserrat}
            fontSize={theme.fontSize.bodyText_1}
            fontWeight={
              value === "monthly"
                ? theme.fontWeight.bold
                : theme.fontWeight.medium
            }
            color={
              value === "monthly"
                ? theme.colors.semantic.semantic_white
                : theme.colors.neutrals.neutrals_70
            }
            sx={{
              transition: "all 0.3s ease"
            }}
          >
            Monthly
          </Typography>
        </Box>

        {/* Yearly Button */}
        <Box
          component="button"
          onClick={() => onChange("yearly")}
          sx={{
            position: "relative",
            zIndex: 1,
            padding: "12px 32px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            transition: "all 0.3s ease",
            borderRadius: "8px",
            minWidth: "140px",
            "&:hover": {
              transform: value === "yearly" ? "none" : "scale(1.02)"
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.colors.secondary.secondary_50}`,
              outlineOffset: "2px"
            }
          }}
        >
          <Typography
            fontFamily={theme.fontFamily.montserrat}
            fontSize={theme.fontSize.bodyText_1}
            fontWeight={
              value === "yearly"
                ? theme.fontWeight.bold
                : theme.fontWeight.medium
            }
            color={
              value === "yearly"
                ? theme.colors.semantic.semantic_white
                : theme.colors.neutrals.neutrals_80
            }
            sx={{
              transition: "all 0.3s ease"
            }}
          >
            Yearly
          </Typography>
        </Box>

        {/* Savings Badge */}
        {savingsPercentage && savingsPercentage > 0 ? (
          <Box
            sx={{
              position: "absolute",
              top: "-12px",
              right: "-12px",
              padding: "4px 10px",
              backgroundColor: theme.colors.semantic.semantic_light_green,
              borderRadius: "12px",
              boxShadow: "0px 2px 8px rgba(76, 175, 80, 0.3)",
              animation:
                value === "yearly" ? "pulse 2s ease-in-out infinite" : "none",
              "@keyframes pulse": {
                "0%, 100%": {
                  transform: "scale(1)"
                },
                "50%": {
                  transform: "scale(1.05)"
                }
              }
            }}
          >
            <Typography
              fontFamily={theme.fontFamily.montserrat}
              fontSize={theme.fontSize.bodyText_3}
              fontWeight={theme.fontWeight.bold}
              color={theme.colors.semantic.semantic_white}
            >
              Save {savingsPercentage}%
            </Typography>
          </Box>
        ) : null}
      </Box>

      {/* Helper text */}
      <Typography
        fontFamily={theme.fontFamily.montserrat}
        fontSize={theme.fontSize.bodyText_2}
        color={theme.colors.neutrals.neutrals_80}
        textAlign="center"
      >
        {value === "yearly"
          ? "🎉 Great choice! You'll save with annual billing"
          : "Switch to yearly billing to save more"}
      </Typography>
    </Stack>
  );
};

export default BillingToggle;
